// apps/web/src/lib/display-mapper.ts
//
// Central display mapper per CISO-surfaces spec §5.1. Single source of
// truth for slug / ISO / acronym → display label conversion. Replaces
// the ad-hoc kebab-to-display logic in vendor-display.ts,
// analytics-compare.tsx, and elsewhere.
//
// In-memory only — does NOT lookup at render time (would tank tile
// performance). Tables are populated at module-load time from baked-in
// constants; future enhancement: hydrate from the ontology registry +
// Phase 1 vendor schema at app boot.

export interface DisplayMapper {
  vendor(slug: string): string;
  country(iso2: string): string;
  sector(slug: string): string;
  framework(slug: string): string;
  threatActor(slug: string): string;
  ransomwareGroup(name: string): string;
  unknownLabel(domain: string): string;
}

// Framework acronyms — uppercase or shaped (NIS2, etc.). Lowercased keys.
const FRAMEWORK_OVERRIDES: Record<string, string> = {
  dora: "DORA",
  nis2: "NIS2",
  gdpr: "GDPR",
  ccpa: "CCPA",
  hipaa: "HIPAA",
  "iso-27001": "ISO 27001",
  "soc-2": "SOC 2",
  "pci-dss": "PCI-DSS",
};

// Threat actor display overrides — slug → polished name.
const THREAT_ACTOR_OVERRIDES: Record<string, string> = {
  lockbit3: "LockBit 3.0",
  lockbit5: "LockBit 5",
  blackcat: "BlackCat",
  cl0p: "Cl0p",
  alphv: "ALPHV",
  apt29: "APT29",
  apt28: "APT28",
};

// Ransomware leak-site group names often arrive lower-case from upstream
// feeds. Keep these lightweight and in-memory; do not lookup during tile render.
const RANSOMWARE_GROUP_OVERRIDES: Record<string, string> = {
  lockbit: "LockBit",
  lockbit2: "LockBit 2.0",
  lockbit3: "LockBit 3.0",
  lockbit5: "LockBit 5",
  blackcat: "BlackCat",
  alphv: "ALPHV",
  cl0p: "Cl0p",
  qilin: "Qilin",
  thegentlemen: "The Gentlemen",
};

// Vendor display overrides — slug → polished name. Will be supplemented
// by a Phase 1 vendor schema lookup once that ships.
const VENDOR_OVERRIDES: Record<string, string> = {
  "auth-zero": "Auth0",
  "palo-alto-networks": "Palo Alto Networks",
  microsoft: "Microsoft",
  cloudflare: "Cloudflare",
  cisco: "Cisco",
  okta: "Okta",
  "red-hat": "Red Hat",
  github: "GitHub",
};

// ISO α2 → English country name. Common-prefix subset; unknowns return ISO.
const COUNTRY_NAMES: Record<string, string> = {
  US: "United States",
  GB: "United Kingdom",
  DE: "Germany",
  FR: "France",
  IT: "Italy",
  ES: "Spain",
  NL: "Netherlands",
  BE: "Belgium",
  PL: "Poland",
  SE: "Sweden",
  FI: "Finland",
  DK: "Denmark",
  NO: "Norway",
  IE: "Ireland",
  PT: "Portugal",
  AT: "Austria",
  CH: "Switzerland",
  CZ: "Czechia",
  GR: "Greece",
  CA: "Canada",
  AU: "Australia",
  NZ: "New Zealand",
  JP: "Japan",
  KR: "South Korea",
  IN: "India",
  BR: "Brazil",
  MX: "Mexico",
  AR: "Argentina",
  ZA: "South Africa",
  AE: "United Arab Emirates",
  SA: "Saudi Arabia",
  IL: "Israel",
  TR: "Turkey",
  RU: "Russia",
  CN: "China",
  TW: "Taiwan",
  SG: "Singapore",
  HK: "Hong Kong",
  ID: "Indonesia",
  TH: "Thailand",
  VN: "Vietnam",
  MY: "Malaysia",
  PH: "Philippines",
};

// Domain → unknownLabel. §4.5 distinguishes unknown from other.
const UNKNOWN_LABELS: Record<string, string> = {
  sector: "Unclassified — see admin queue",
  vendor: "Unclassified — see admin queue",
  framework: "Unclassified — see admin queue",
  threatActor: "Unclassified — see admin queue",
  country: "Unknown country",
};

function titleCase(input: string): string {
  return input
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function overrideKey(input: string): string {
  return input.toLowerCase().replace(/[^a-z0-9]/g, "");
}

export const displayMapper: DisplayMapper = {
  vendor(slug: string): string {
    if (!slug) return UNKNOWN_LABELS.vendor;
    const k = slug.toLowerCase();
    return VENDOR_OVERRIDES[k] ?? titleCase(slug);
  },
  country(iso2: string): string {
    if (!iso2) return UNKNOWN_LABELS.country;
    return COUNTRY_NAMES[iso2.toUpperCase()] ?? iso2;
  },
  sector(slug: string): string {
    if (!slug) return UNKNOWN_LABELS.sector;
    return titleCase(slug);
  },
  framework(slug: string): string {
    if (!slug) return UNKNOWN_LABELS.framework;
    const k = slug.toLowerCase();
    return FRAMEWORK_OVERRIDES[k] ?? titleCase(slug);
  },
  threatActor(slug: string): string {
    if (!slug) return UNKNOWN_LABELS.threatActor;
    const k = slug.toLowerCase();
    return THREAT_ACTOR_OVERRIDES[k] ?? THREAT_ACTOR_OVERRIDES[overrideKey(slug)] ?? titleCase(slug);
  },
  ransomwareGroup(name: string): string {
    if (!name) return UNKNOWN_LABELS.threatActor;
    const k = name.toLowerCase();
    return RANSOMWARE_GROUP_OVERRIDES[k] ?? RANSOMWARE_GROUP_OVERRIDES[overrideKey(name)] ?? titleCase(name);
  },
  unknownLabel(domain: string): string {
    return UNKNOWN_LABELS[domain] ?? "Unclassified";
  },
};
